import { CheckCircleIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/solid';
import React from 'react';

import AccessibleEmoji from '../AccessibleEmoji';
import Popup from './Popup.module';

interface InDevelopmentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  highlights?: string[];
  etaLabel?: string;
  primaryLabel?: string;
  onPrimary?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  learnMoreUrl?: string;
}

function InDevelopmentPopup({
  isOpen,
  onClose,
  title = 'Fonctionnalité en développement',
  description = 'Cette fonctionnalité est en préparation. Revenez très bientôt pour la découvrir.',
  highlights,
  etaLabel = 'Bientôt disponible',
  primaryLabel = 'Compris',
  onPrimary,
  secondaryLabel,
  onSecondary,
  learnMoreUrl,
}: InDevelopmentPopupProps) {
  const handlePrimary = () => {
    if (onPrimary) { onPrimary(); } else { onClose(); }
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      additionalClass="w-[min(720px,92vw)] max-w-[92vw]"
      title={
        <div className="flex items-center gap-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-rgpd-blue-light text-rgpd-blue">
            <SparklesIcon className="h-5 w-5" />
          </span>
          <span className="flex items-center gap-2">
            <span>{title}</span>
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-rgpd-blue/10 px-2 py-0.5 text-xs font-semibold text-rgpd-blue">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rgpd-blue opacity-50" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rgpd-blue" />
              </span>
              {etaLabel}
            </span>
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Intro card */}
        <div className="rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 p-5">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-indigo-100 bg-white/80 shadow-sm backdrop-blur-sm">
                <AccessibleEmoji symbol={<span role="img" aria-label="construction">🚧</span>} label="en développement" className="text-3xl" />
              </div>
            </div>
            <div className="flex-1">
              <p className="leading-relaxed text-gray-700">{description}</p>
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <ClockIcon className="h-4 w-4 text-indigo-400" />
                <span>Disponible prochainement</span>
              </div>
            </div>
          </div>
        </div>

        {/* Highlights */}
        {Array.isArray(highlights) && highlights.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {highlights.map((item, idx) => (
              <div key={idx} className="flex items-start gap-2 rounded-lg border border-gray-100 bg-white p-3">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 text-emerald-500" />
                <p className="text-sm leading-relaxed text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col items-stretch justify-end gap-3 sm:flex-row sm:items-center">
          {secondaryLabel && (
            <button
              className="h-11 rounded-md border border-gray-300 bg-white px-5 text-gray-700 transition-colors hover:bg-gray-50"
              onClick={onSecondary || onClose}
            >
              {secondaryLabel}
            </button>
          )}

          <button
            className="h-11 rounded-md bg-rgpd-blue px-5 text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark"
            onClick={handlePrimary}
          >
            {primaryLabel}
          </button>
        </div>

        {learnMoreUrl && (
          <div className="text-right">
            <a
              href={learnMoreUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              En savoir plus
            </a>
          </div>
        )}
      </div>
    </Popup>
  );
}

export default InDevelopmentPopup;


